/**
 * NexusLayer SSO for Vestige.
 *
 * The token is captured at module scope, on first import, straight from the
 * URL. It has to happen this early: any module that rewrites the URL during its
 * own import (stripping query params, normalising the path) runs before the
 * importing module's body and would take the token with it. BrainVault lost a
 * debugging session to exactly that.
 */

const TOKEN_KEY = 'vestige_token'
const IDENTITY_URL = 'https://identity.nexuslayer.eu'

// Read-and-clear, before anything else can touch location.
const _capturedToken = (() => {
  try {
    const params = new URLSearchParams(window.location.search)
    const token = params.get('sso_token')
    if (!token) return null

    window.localStorage.setItem(TOKEN_KEY, token)

    // Drop the token from the address bar so it stays out of history,
    // bookmarks and any Referer we later send.
    params.delete('sso_token')
    const qs = params.toString()
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash
    )
    return token
  } catch (err) {
    console.error('SSO token capture failed:', err)
    return null
  }
})()

export const getToken = () => {
  if (_capturedToken) return _capturedToken
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch (err) {
    console.error('Could not read stored SSO token:', err)
    return null
  }
}

export const clearToken = () => {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
  } catch (err) {
    console.error('Could not clear stored SSO token:', err)
  }
}

/**
 * Decodes the JWT payload for display only — this does NOT verify the
 * signature. Anything that matters is authorised server-side; the claims are
 * used here purely to render a name and tier without an extra round trip.
 */
export const decodeToken = (token) => {
  if (!token) return null
  try {
    const [, payload] = token.split('.')
    if (!payload) return null
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const claims = JSON.parse(json)
    return {
      // NexusLayer puts the email in `sub`. There is no `email` claim.
      email: claims.sub,
      name: claims.name || claims.sub,
      role: claims.role || 'USER',
      tier: claims.tier || 'FREE',
      expiresAt: claims.exp ? claims.exp * 1000 : null,
    }
  } catch (err) {
    console.error('Could not decode SSO token:', err)
    return null
  }
}

export const isExpired = (user) =>
  Boolean(user?.expiresAt) && user.expiresAt <= Date.now()

/** Current user from the stored token, or null when absent/expired. */
export const getUser = () => {
  const user = decodeToken(getToken())
  if (!user || isExpired(user)) return null
  return user
}

/** Hand off to the identity server, which returns here with ?sso_token=. */
export const login = () => {
  const redirect = `${window.location.origin}${window.location.pathname}`
  window.location.href = `${IDENTITY_URL}/login?redirect=${encodeURIComponent(redirect)}`
}

export const logout = () => {
  clearToken()
  login()
}
